/**
 * ============================================================================
 * Presenter — push an item to the room and show what comes back
 * ============================================================================
 * This is the screen on the projector, so two things matter more than usual.
 *
 * NO IDENTITY APPEARS HERE, EVER. The backend aggregates before it responds and
 * never returns a token, so there is nothing to leak by accident. Free-text
 * answers are real feedback from named people; a name beside one on a wall
 * changes what anybody is willing to write next.
 *
 * FREE TEXT SHOWS THE WORDS, NOT A COUNT. "12 responses" tells a room nothing.
 * The responses ARE the discussion — that is the point of asking.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import MaterialsShelf from '../components/MaterialsShelf.jsx';
import LiveResponses from '../components/LiveResponses.jsx';
import SlideDeck from '../components/SlideDeck.jsx';
import { deckForClass } from '../decks/attestDemo.js';

export function PresenterPage() {
  const { id } = useParams();

  const [questions, setQuestions] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [responses, setResponses] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [classId, setClassId] = useState(null);

  const loadQuestions = useCallback(async () => {
    const data = await apiFetch(`/api/v1/sessions/${id}/questions`);
    setQuestions(data.questions ?? []);
    setActiveId(data.active_question_id ?? null);
    if (data.class_id) setClassId(data.class_id);
  }, [id]);

  useEffect(() => {
    loadQuestions().catch(setError);
  }, [loadQuestions]);

  /** The section's own deck (M45). A failed read falls back, it does not block Present. */
  const [storedDeck, setStoredDeck] = useState(null);
  useEffect(() => {
    if (!classId) return;
    apiFetch(`/api/v1/classes/${classId}/deck`)
      .then((d) => setStoredDeck(d?.deck?.slides?.length ? { ...d.deck, title: d.deck.title || 'Slides' } : null))
      .catch(() => setStoredDeck(null));
  }, [classId]);

  // Answers arrive while the room is answering, so the tally has to move on its
  // own. Failures are swallowed: a blink of Wi-Fi must not blank a live tally.
  useEffect(() => {
    if (!activeId) {
      setResponses(null);
      return undefined;
    }
    let active = true;
    const tick = () =>
      apiFetch(`/api/v1/sessions/${id}/responses?question_id=${activeId}`)
        .then((data) => active && setResponses(data))
        .catch(() => {});
    tick();
    const timer = setInterval(tick, 2000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [id, activeId]);

  async function push(questionId) {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/sessions/${id}/push`, {
        method: 'POST',
        body: JSON.stringify({ question_id: questionId }),
      });
      setActiveId(questionId);
      setResponses(null);
      if (questionId) {
        setQuestions((qs) => qs.map((q) => (q.id === questionId && !q.pushed_at ? { ...q, pushed_at: new Date().toISOString() } : q)));
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const active = (questions ?? []).find((q) => q.id === activeId) ?? null;
  // A section with a deck (M39, 2026-09-22) presents as slides: the lead-in,
  // the push buttons at the foot, the reveal on the slide. Without one the
  // plain list below is what it always was.
  // Since M45 the deck lives on the section; the one baked in for demo day
  // is the fallback for the sections it was written for.
  const deck = storedDeck ?? deckForClass(classId);

  return (
    <>
      <p className="crumb">
        <Link to={`/sessions/${id}/roster`}>← Who’s here</Link>
      </p>

      <h1>{deck ? deck.title : 'Discussion items'}</h1>
      <ErrorBanner error={error} />

      {deck && questions !== null && (
        <SlideDeck
          deck={deck}
          sessionId={id}
          questions={questions}
          activeId={activeId}
          responses={responses}
          busy={busy}
          onPush={push}
        />
      )}

      {/* The shelf (M26): the slides and video are here, beside the items,
          because this is the screen on the projector. */}
      {classId && (
        <details className="panel shelf__panel">
          <summary>
            <strong>Materials</strong> <span className="muted">— slides, video, handouts</span>
          </summary>
          <MaterialsShelf classId={classId} compact />
        </details>
      )}

      {!deck && (
      <section className="panel" aria-labelledby="items-heading">
        <h2 id="items-heading">Items</h2>
        <p className="muted">
          One item is live at a time. Pushing a new one moves everybody’s phone to it.
        </p>

        {questions === null && (
          <p className="muted" aria-busy="true">
            Loading…
          </p>
        )}

        <ol className="qlist">
          {(questions ?? []).map((q) => (
            <li
              key={q.id}
              className={q.id === activeId ? 'qlist__item qlist__item--live' : 'qlist__item'}
            >
              <span className="qlist__stem">
                {q.stem}
                <span className="muted"> · {q.type.replace(/_/g, ' ')}</span>
                {/* Which items decide credit, and which the room has already
                    seen — so the instructor can see at a glance that the final
                    assessment has not gone up yet. */}
                {q.is_completion_check && <span className="badge badge--published"> completion check</span>}
                {q.pushed_at && q.id !== activeId && <span className="muted"> · asked</span>}
              </span>
              <button
                type="button"
                className={q.id === activeId ? 'btn btn--auto' : 'btn btn--primary btn--auto'}
                disabled={busy}
                onClick={() => push(q.id === activeId ? null : q.id)}
              >
                {q.id === activeId ? 'Take down' : 'Push to room'}
              </button>
            </li>
          ))}
        </ol>
      </section>
      )}

      {active && !deck && (
        <section className="panel" aria-labelledby="live-heading">
          <h2 id="live-heading">On screen now</h2>
          <p className="qlive__stem">{active.stem}</p>
          <LiveResponses responses={responses} />

          <p className="muted">Answers are anonymous on this screen — no names, no IDs.</p>
        </section>
      )}
    </>
  );
}

export default PresenterPage;
